// The thread workspace's record panel: a pull-open summary of the customer/job
// (phone · vehicle · appointment · service · lifetime value) with `SET BY CHAT`
// tags on whatever Eric set. Collapsed by default; expands in place.
//
// Motion (emil-design-eng): a CSS-only grid-rows height reveal, gated by
// prefers-reduced-motion. Full-border card, no side-stripes (impeccable).
// Aesthetic (Phase 5, home-scoped): electric-indigo accent + mono numerics.
// Data comes from GET /threads/:id `record` (one round-trip).

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ExternalLink } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { formatMoney, formatPhone } from '@/lib/format'
import { formatDateTime } from '@/lib/dates'
import type { ThreadDetail, ThreadRecord } from '@/lib/threads'

function SetByChatTag() {
  const { t } = useTranslation('dashboard')
  return (
    <span className="ml-1.5 inline-block rounded bg-hv-accent-soft px-1 py-px align-middle font-mono text-[9px] font-medium uppercase tracking-wider text-hv-accent">
      {t('home_v2.set_by_chat')}
    </span>
  )
}

function KV({
  label,
  value,
  sub,
  chatSet,
  mono,
}: {
  label: string
  value: string
  sub?: string | null
  chatSet?: boolean
  mono?: boolean
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border/60 py-2 last:border-b-0">
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <span className="flex min-w-0 flex-col items-end text-right">
        <span className={`text-sm font-medium text-foreground ${mono ? 'font-mono' : ''}`}>
          {value}
          {chatSet ? <SetByChatTag /> : null}
        </span>
        {sub ? (
          <span className={`text-xs text-muted-foreground ${mono ? 'font-mono' : ''}`}>
            {sub}
          </span>
        ) : null}
      </span>
    </div>
  )
}

export function RecordPanel({
  thread,
  record,
}: {
  thread: ThreadDetail['thread']
  record: ThreadRecord
}) {
  const { t } = useTranslation('dashboard')
  const [open, setOpen] = useState(false)

  const name = thread.customer_name ?? thread.title ?? t('home_v2.new_chat')
  const vehicleLabel = record.vehicle?.label ?? thread.vehicle_label
  const customerChat = record.set_by_chat.includes('customer')
  const vehicleChat = record.set_by_chat.includes('vehicle')

  return (
    <div className="rounded-xl border border-border bg-card">
      <Button
        type="button"
        variant="ghost"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex min-h-11 w-full items-center justify-between gap-3 whitespace-normal rounded-none px-4 py-3 text-left font-normal [@media(hover:hover)_and_(pointer:fine)]:hover:bg-transparent aria-expanded:bg-transparent dark:hover:bg-transparent"
      >
        <span className="min-w-0">
          <span className="block truncate text-base font-semibold text-foreground">
            {name}
          </span>
          {vehicleLabel ? (
            <span className="block truncate font-mono text-sm text-muted-foreground">
              {vehicleLabel}
            </span>
          ) : null}
        </span>
        <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-hv-accent">
          {open ? t('home_v2.hide_record') : t('home_v2.record')}
          <ChevronDown
            className={`size-4 transition-transform duration-200 ease-out motion-reduce:transition-none ${
              open ? 'rotate-180' : ''
            }`}
          />
        </span>
      </Button>

      <div
        className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="border-t border-border px-4">
            {record.customer?.phone ? (
              <KV
                label={t('home_v2.rec_phone')}
                value={formatPhone(record.customer.phone)}
                chatSet={customerChat}
                mono
              />
            ) : null}
            {vehicleLabel ? (
              <KV
                label={t('home_v2.rec_vehicle')}
                value={vehicleLabel}
                sub={record.vehicle?.vin}
                chatSet={vehicleChat}
                mono
              />
            ) : null}
            {record.appointment ? (
              <KV
                label={t('home_v2.rec_appointment')}
                value={
                  record.appointment.starts_at
                    ? formatDateTime(record.appointment.starts_at)
                    : '—'
                }
                sub={record.appointment.status}
                mono
              />
            ) : null}
            {record.appointment?.notes ? (
              <KV label={t('home_v2.rec_service')} value={record.appointment.notes} />
            ) : null}
            {record.customer?.lifetime_value_cad != null ? (
              <KV
                label={t('home_v2.rec_lifetime')}
                value={formatMoney(record.customer.lifetime_value_cad)}
                mono
              />
            ) : null}
          </div>

          {record.customer ? (
            <Link
              to={`/customers/${record.customer.id}`}
              className="mx-4 mb-3 mt-1 inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-hv-accent hover:underline"
            >
              {t('home_v2.open_record')}
              <ExternalLink className="size-3.5" />
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  )
}
